// Package imports:
import React, { useEffect, useState } from 'react';
// Service imports:
import { formatDate } from '../../../services/utils';
import { fetchAlertReviewAttachments } from '../../../redux-store/actions/fetchAlertReviewAttachments';
// Type imports:
import { AlertReviewHistoryByAlertDataId, AlertTickData } from '../../../types/api/AlertTypes';

interface IOwnProps {
    alert: AlertTickData,
    alertReviewHistoryByAlertDataId: AlertReviewHistoryByAlertDataId,
    accessTokenV2: string
}
type Props = IOwnProps;

/**
 * Component to display the history of an alert. **Found at Review Alert History**
 * - Subcomponent of HomeAlertSingleReviewModal.tsx
 */
const AlertHistoryV2: React.FC<Props> = ({
    alert,
    alertReviewHistoryByAlertDataId,
    accessTokenV2
}) => {
    const alertDataId = alert.ad_id || -1;
    const alertReviewHistory = alertReviewHistoryByAlertDataId[alertDataId];
    if (alertReviewHistory === undefined || alertReviewHistory === 'waiting') {
        return (
            <p>Loading...</p>
        );
    }
    if (alertReviewHistory instanceof Error) {
        return (
            <p>Error: {alertReviewHistory.message}</p>
        );
    }

    const formatStatus = (key: string) => {
        var stateColor = '';
        var stateToolTip = '';
        switch (key) {
            case "closed":
                stateColor = '#0653c8';
                stateToolTip = 'Closed';
                break;
            case "critical":
                stateColor = '#e30f0f';
                stateToolTip = 'Critical';
                break;
            case "explained":
                stateColor = '#b110b5';
                stateToolTip = 'Explained';
                break;
            case "forreview":
                stateColor = '#e3e3e3';
                stateToolTip = 'For Review';
                break;
            case "investig":
                stateColor = '#bdaa00';
                stateToolTip = 'Investigating';
                break;
            case "open":
                stateColor = '#00938a';
                stateToolTip = 'Open';
                break;
            case "watch":
                stateColor = '#197a3a';
                stateToolTip = 'Watch';
                break;
            default:
                stateColor = '#000000';
        }

        return (
          <span
            title={stateToolTip}
            className={`status-dot ${key}`}
          ></span>
        );
    }

    let reviewHistoryArray: any[] | undefined = undefined;
    if (Array.isArray(alertReviewHistory)) {
        reviewHistoryArray = alertReviewHistory;
    } else if (alertReviewHistory && Array.isArray((alertReviewHistory as any).data)) {
        reviewHistoryArray = (alertReviewHistory as any).data;
    }
    // State to store attachments for each review
    const [attachmentsMap, setAttachmentsMap] = useState<Record<number, any[]>>({});

    useEffect(() => {
        if (!reviewHistoryArray) return;
        reviewHistoryArray.forEach(async (entry: any) => {
            if (!entry.review_id || attachmentsMap[entry.review_id]) return;
            // Only fetch if not already fetched
            const groupId = entry.groupid || entry.adu_groupid || alert.adu_groupid;
            if (!groupId) return;
            const files = await fetchAlertReviewAttachments(entry.review_id, groupId, accessTokenV2);
            setAttachmentsMap(prev => ({ ...prev, [entry.review_id]: files }));
        });
    }, [JSON.stringify(reviewHistoryArray), accessTokenV2]);

    // Sort by comment_timestamp descending (newest first)
    reviewHistoryArray = (reviewHistoryArray ?? []).slice().sort((a, b) => {
        const aTime = new Date(a.comment_timestamp).getTime();
        const bTime = new Date(b.comment_timestamp).getTime();
        return bTime - aTime;
    });

    return (
        <div className='AlertHistory-container'>
            {
                reviewHistoryArray.map((alertReviewHistoryEntry: any, idx: number) => (
                    <div key={alertReviewHistoryEntry.review_id ? alertReviewHistoryEntry.review_id : idx} className='AlertHistory-entry'>
                        <div className='AlertHistory-entry-state'>{formatStatus(alertReviewHistoryEntry.status)}</div>
                        <div className='AlertHistory-entry-header'>{alertReviewHistoryEntry.username} • {formatDate(alertReviewHistoryEntry.comment_timestamp)}</div>
                        <div></div>
                        <div className='AlertHistory-entry-body'>{alertReviewHistoryEntry.alert_comment}</div>
                        <div></div>
                        <div className="AlertReviewAttachments">
                            <ul>
                            {(attachmentsMap[alertReviewHistoryEntry.review_id] && attachmentsMap[alertReviewHistoryEntry.review_id].length > 0) ? (
                                attachmentsMap[alertReviewHistoryEntry.review_id].map((file: any, idx: number) => {
                                    const downloadUrl = file.url;
                                    return (
                                    <li key={file.s3_key || idx}>
                                        <a
                                        href={downloadUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        download={file.filename}
                                        title={file.filename}
                                        >
                                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                            <path d="M12 16V4M12 16l-4-4M12 16l4-4M4 20h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        {file.filename} {file.file_size_bytes ? `(${Math.round(file.file_size_bytes/1024)} KB)` : ''}
                                        </a>
                                    </li>
                                    );
                                })
                            ) : null}
                            </ul>
                        </div>
                    </div>
                ))
            }
        </div>
    )
}

export default AlertHistoryV2;